"use client";

import { OTPInput, OTPInputContext, REGEXP_ONLY_DIGITS } from "input-otp";
import * as React from "react";
import { cn } from "../utils/index";

/*
 * A run of digits in a paste. A single space or dash may sit between two
 * digits, so a grouped code ("123 456", "12-34-56") stays one run.
 */
const DIGIT_RUN = /\d(?:[ -]?\d)*/g;

/*
 * Picks the code out of a pasted SMS or email: the last run with exactly the
 * field's length, so "Your 6-digit code is 482913" gives 482913, not the
 * 648291 that joining every digit would auto-submit. A paste holding a single
 * run shorter than the field keeps it, so a partial code still fills the first
 * slots. A single longer run ("Step 1 482913" is one run) would be cut to the
 * field's length and auto-submitted, so it pastes nothing. Anything else is
 * ambiguous and pastes nothing too, which is what input-otp does without a
 * transformer.
 */
function pickCode(pasted: string, maxLength: number) {
  const codes = (pasted.match(DIGIT_RUN) ?? []).map((run) =>
    run.replace(/\D/g, ""),
  );
  for (let index = codes.length - 1; index >= 0; index -= 1) {
    if (codes[index].length === maxLength) {
      return codes[index];
    }
  }
  if (codes.length === 1 && codes[0].length < maxLength) {
    return codes[0];
  }
  return "";
}

/*
 * A digits-only field picks the code out of a formatted paste. Without this,
 * input-otp tests the whole pasted string against the pattern and drops it.
 * A consumer's own pasteTransformer always wins.
 */
function getPasteTransformer(
  pattern: string | undefined,
  pasteTransformer: ((pasted: string) => string) | undefined,
  maxLength: number,
) {
  if (pasteTransformer) {
    return pasteTransformer;
  }
  if (pattern === REGEXP_ONLY_DIGITS) {
    return (pasted: string) => pickCode(pasted, maxLength);
  }
  return undefined;
}

function InputOTP({
  className,
  containerClassName,
  pattern,
  pasteTransformer,
  maxLength,
  ...props
}: React.ComponentProps<typeof OTPInput> & {
  containerClassName?: string;
}) {
  /*
   * Not upstream: a code reads left to right in every language, so the slots
   * stay in that order on right-to-left pages instead of the first digit
   * landing in the rightmost slot.
   */
  return (
    <OTPInput
      data-slot="input-otp"
      containerClassName={cn(
        "cn-input-otp flex items-center gap-2 [direction:ltr] has-disabled:opacity-50",
        containerClassName,
      )}
      dir="ltr"
      spellCheck={false}
      className={cn("disabled:cursor-not-allowed", className)}
      pattern={pattern}
      maxLength={maxLength}
      pasteTransformer={getPasteTransformer(
        pattern,
        pasteTransformer,
        maxLength,
      )}
      {...props}
    />
  );
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-otp-group"
      className={cn(
        "flex items-center rounded-lg has-aria-invalid:ring-3 has-aria-invalid:ring-destructive/20",
        className,
      )}
      {...props}
    />
  );
}

function InputOTPSlot({
  index,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  index: number;
}) {
  const inputOTPContext = React.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};

  return (
    <div
      data-slot="input-otp-slot"
      data-active={isActive}
      className={cn(
        "relative flex size-12 items-center justify-center border-y border-r border-border bg-background text-sm text-foreground transition-all outline-none first:rounded-l-lg first:border-l last:rounded-r-lg aria-invalid:border-destructive data-[active=true]:z-10 data-[active=true]:border-ring data-[active=true]:ring-3 data-[active=true]:ring-ring/25 data-[active=true]:aria-invalid:border-destructive data-[active=true]:aria-invalid:ring-destructive/20",
        className,
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-caret-blink bg-foreground duration-1000" />
        </div>
      )}
    </div>
  );
}

function InputOTPSeparator({ ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-otp-separator"
      className="h-px w-6 shrink-0 bg-border"
      role="separator"
      {...props}
    />
  );
}

export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot };
